// EXEMPT
import { useTranslation } from "react-i18next";

import type { DigAppState } from "@/__main__/app-state.mjs";
import { readState } from "@/__main__/app-state.mjs";
import { isPopulatedFromDB, PATH_DELIMITER } from "@/__main__/constants.mjs";
import type { TransformModelFn } from "@/__main__/data-model.mjs";
import dbObj, {
  __PRIVATE_DB_SYMBOL_FOR_INTERNAL_USE_NOT_APPLICATION_CODE,
} from "@/__main__/db.mjs";
import type { DataSource, PostDataRequest } from "@/__main__/get-data.mjs";
import getData, { postData } from "@/__main__/get-data.mjs";
import type { HubRouteMeta } from "@/__main__/router.mjs";
import router from "@/__main__/router.mjs";
import {
  ACTIVE_GAMES,
  appURLs,
  GAME_SHORT_NAMES,
  GAME_SYMBOL_BY_SHORT_NAME,
} from "@/app/constants.mjs";
import type { GamesWithProp, GameSymbol } from "@/app/games.mjs";
import type { GameHash } from "@/app/initial-state-defaults.mjs";
import type { AppSettings } from "@/data-models/app-settings.mjs";
import noopModel from "@/data-models/no-op.mjs";
import { GAME_SYMBOL_APEX } from "@/game-apex/definition-symbol.mjs";
import apexStatic from "@/game-apex/static.mjs";
import { GAME_SYMBOL_CS2 } from "@/game-csgo/definition-symbol.mjs";
import { getProfileAvatar as getCS2ProfileAvatar } from "@/game-csgo/utils.mjs";
import { GAME_SYMBOL_DEADLOCK } from "@/game-deadlock/definition-symbol.mjs";
import { GAME_SYMBOL_EFT } from "@/game-eft/definition-symbol.mjs";
import getEFTProfileAvatar from "@/game-eft/utils/getProfileAvatar.mjs";
import { GAME_SYMBOL_FORTNITE } from "@/game-fortnite/definition-symbol.mjs";
import StaticFortnite from "@/game-fortnite/utils/static.mjs";
import { SERVICES_TO_REGIONS } from "@/game-lol/constants/constants.mjs";
import { GAME_SYMBOL_LOL } from "@/game-lol/definition-symbol.mjs";
import StaticLOL from "@/game-lol/utils/static.mjs";
import { GAME_SYMBOL_MARVEL_RIVALS } from "@/game-marvel-rivals/definition-symbol.mjs";
import { GAME_SYMBOL_TFT } from "@/game-tft/definition-symbol.mjs";
import { GAME_SYMBOL_VAL } from "@/game-val/definition-symbol.mjs";
import { getProfileIcon as getValProfileIcon } from "@/game-val/static.mjs";
import { formatProfileForNav } from "@/game-val/utils.mjs";
import { devError, devWarn } from "@/util/dev.mjs";
import gameShortNameReassign from "@/util/game-short-name-reassign.mjs";
import { getLocale } from "@/util/i18n-helper.mjs";
import isEmpty from "@/util/is-empty.mjs";
import orderArrayBy from "@/util/order-array-by.mjs";
import rangeBucket from "@/util/range-bucket.mjs";
import { useRouteComponent } from "@/util/router-hooks.mjs";
import unionBy from "@/util/union-by.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

type ApiObject =
  | object
  | {
      id?: string;
      updatedAt?: Date;
    };

type DigApiObject<P extends readonly PropertyKey[]> = DigAppState<P> &
  ApiObject;

type MergeObject<L extends object, R extends object> = L | R | Merge<L, R>;

export const mergeLocalWithApi = <C extends ApiObject, N extends ApiObject>(
  curValue: C,
  newValue: N,
): MergeObject<C, N> => {
  if (!curValue || curValue instanceof Error) return newValue;
  if (!newValue || newValue instanceof Error) return curValue;

  if (newValue["updatedAt"] > curValue["updatedAt"])
    return {
      ...curValue,
      ...newValue,
    };

  return {
    ...newValue,
    ...curValue,
  };
};

type MergeNestedObject<
  L extends Record<PropertyKey, ApiObject>,
  R extends Record<PropertyKey, ApiObject>,
> =
  | L
  | R
  | {
      [K in keyof L | keyof R]: K extends keyof L
        ? K extends keyof R
          ? Unwrap<MergeObject<L[K], R[K]>>
          : L[K]
        : K extends keyof R
          ? R[K]
          : never;
    };

export const mergeNestedObjWithApi = <
  C extends Record<PropertyKey, ApiObject>,
  N extends Record<PropertyKey, ApiObject>,
>(
  curValue: C,
  newValue: N,
): MergeNestedObject<C, N> => {
  if (!curValue || curValue instanceof Error) return newValue;
  if (!newValue || newValue instanceof Error) return curValue;

  const allKeys = new Set([...Object.keys(curValue), ...Object.keys(newValue)]);
  const newObj = {};

  for (const key of allKeys) {
    newObj[key] = mergeLocalWithApi(curValue?.[key], newValue?.[key]);
  }

  return newObj as MergeNestedObject<C, N>;
};

type UpdateQueryData<K extends string, N extends object> = { id: string } & {
  [P in K]: N;
};

type UpsertLocalDataProps<
  P extends readonly PropertyKey[],
  K extends string,
  N extends object,
> = {
  curValue: DigAppState<P> & { id?: string };
  newValue?: N;
  updateKey?: K;
  nestedId?: string;
  writeStatePath: P;

  getQuery: DataSource<DigApiObject<P>>;
  createQuery: PostDataRequest;

  shouldUpdate?: (curValue: DigApiObject<P>, newValue: N) => boolean;
  updateQuery?: (data: UpdateQueryData<K, N>) => PostDataRequest;

  model: TransformModelFn<unknown, DigApiObject<P>>;

  mergeFn?: (
    curValue: DigApiObject<P>,
    newValue: DigApiObject<P>,
  ) => DigApiObject<P>;
  getOptions?: object;
};

export const mergeArrayObjectsById = (
  curValue,
  newValue,
  idField,
  sortField = idField,
) => {
  return curValue && !(curValue instanceof Error)
    ? orderArrayBy(unionBy(curValue, newValue, idField), sortField, "desc")
    : newValue;
};

/**
 * @deprecated
 * Cursed method: too many unnecessary args, swallows errors.
 * ==========================================================
 * For sending reversed/other data to BE
 *
 */
export const upsertLocalData = async <
  const P extends readonly PropertyKey[],
  const K extends string,
  N extends object,
>({
  curValue,
  newValue,
  updateKey,
  nestedId,
  writeStatePath,
  getQuery,
  createQuery,
  updateQuery,
  shouldUpdate,
  model,
  // not possible to reconcile currently
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mergeFn = mergeLocalWithApi as any,
  getOptions,
}: UpsertLocalDataProps<P, K, N>) => {
  // id is our internal id BE, its existence means that this data has already been created BE
  let id = curValue?.id;
  let oldValue = curValue;
  let err;

  // If there is no id cached locally, fetch
  if (!id && getQuery) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await getData(getQuery, model as any, writeStatePath, {
      skipSafetyCheck: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      mergeFn: mergeFn as any,
      ...getOptions,
    }).catch((e) => (err = e));

    oldValue = nestedId ? response?.[nestedId] : response;

    id = oldValue?.["id"];
  }

  if (!id && !err?.message?.includes("no records found")) return;

  // If id exists, diff and update if necessary
  if (id && updateKey && shouldUpdate(oldValue, newValue)) {
    postData(
      updateQuery({ id, [updateKey]: newValue } as UpdateQueryData<K, N>),
      noopModel,
      undefined,
      {
        skipLoadingPlaceholder: true,
      },
    ).catch(devWarn);
    // If no id exists, create
  } else if (!id && createQuery) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await postData(createQuery, model as any, writeStatePath, {
      skipLoadingPlaceholder: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      mergeFn: mergeFn as any,
    }).catch(devWarn);

    id = response?.["id"];
  }

  return id;
};

interface GameProfileImgObjectMap {
  [GAME_SYMBOL_APEX]: { hoveredChampionApexId: string };

  [GAME_SYMBOL_LOL]: {
    profileIconId: number;
  };

  [GAME_SYMBOL_TFT]: {
    profileIconId: number;
  };

  [GAME_SYMBOL_VAL]: {
    lastAgent: { name: string } | null;
    playerCardId: string | null;
  };

  [GAME_SYMBOL_CS2]: never;

  [GAME_SYMBOL_FORTNITE]: {
    accountId: string;
    character: string;
  };
}

export function getGameProfileImg(
  ...args: OmitNever<Entry<GameProfileImgObjectMap>>
): string {
  const [game, obj] = args;

  if (game === GAME_SYMBOL_APEX) {
    return apexStatic.getLegendImage(obj.hoveredChampionApexId);
  }

  if (game === GAME_SYMBOL_VAL) {
    return getValProfileIcon(obj.playerCardId, obj.lastAgent?.name);
  }

  if (game === GAME_SYMBOL_CS2) {
    return getCS2ProfileAvatar();
  }

  if (game === GAME_SYMBOL_LOL || game === GAME_SYMBOL_TFT) {
    return StaticLOL.getProfileIcon(obj.profileIconId);
  }

  if (game === GAME_SYMBOL_FORTNITE) {
    return StaticFortnite.getProfileImage(obj.accountId, obj.character);
  }

  if (game === GAME_SYMBOL_EFT) {
    return getEFTProfileAvatar();
  }

  devError("`getGameProfileImg` does not support", game);

  return "";
}

// Not exporting because private.
const __languageToRegionMap = {
  en: "en_US",
  de: "de_DE",
  es: "es_ES",
  fr: "fr_FR",
  it: "it_IT",
  pt: "pt_BR",
  tr: "tr_TR",
  pl: "pl_PL",
  ru: "ru_RU",
  cs: "cs_CZ",
  cz: "cs_CZ",
  el: "el_GR",
  gr: "el_GR",
  ko: "ko_KR",
  kr: "ko_KR",
  ja: "ja_JP",
  jp: "ja_JP",
  vi: "vi_VN",
  vn: "vi_VN",
  "zh-Hans-CN": "zh_CN",
  "zh-Hant-TW": "zh_TW",
} as const;
const __defaultRitoLanguage = __languageToRegionMap["en"];

export function getRitoLanguageCodeFromBCP47(code: string): string {
  const result = __languageToRegionMap[code];
  return result || __defaultRitoLanguage;
}

// transforms the language region map to return the region-based locale
// most notably cz -> cs, gr -> el, kr -> ko, jp -> ja
// with the exception of vn for which vi is used
// and zh is passed through
// passthrough mappings are required because all other langauges (incl. hu, th, nl) fall back to en
const __localeMap = Object.assign(
  Object.fromEntries(
    Object.entries(__languageToRegionMap).map(([key, value]) =>
      key.length > 2 ? [key, key] : [key, value.slice(0, 2)],
    ),
  ),
  {
    vn: "vi",
    vi: "vi",
  },
);
const __defaultLocale = __localeMap["en"];

// returns valid locale for static translation
// idempotent
export function getRitoLocale(locale = getLocale()): string {
  return __localeMap[locale] || __defaultLocale;
}

const lol5v5Ranges = rangeBucket([
  [0, "var(--perf-neg3)"],
  [46, "var(--perf-neg2)"],
  [48, "var(--perf-neg1)"],
  [50, "var(--perf-neutral)"],
  [51, "var(--perf-pos1)"],
  [53, "var(--perf-pos2)"],
  [55, "var(--perf-pos3)"],
]);

export const winRatecolorRange = (percent) => lol5v5Ranges[percent];
/**
 * DO NOT USE THIS FUNCTION DIRECTLY UNLESS YOU ARE WRITING DATA OUTSIDE
 * OF APP-STATE!
 *
 * Unless the data you are persisting exists outside of app-state,
 * there is no reason this should be used. This mainly exists as an
 * escape hatch in case there is data that is managed externally from
 * app-state.
 *
 * Bypassing writeState skips a few internal checks to make sure that
 * paths written to are conflict-free.
 */
export function __INTERNAL_PERSIST_METHOD(data, path) {
  if (data?.[isPopulatedFromDB]) return null;
  const db = dbObj[__PRIVATE_DB_SYMBOL_FOR_INTERNAL_USE_NOT_APPLICATION_CODE];
  const id = path.join(PATH_DELIMITER);
  return db.upsert([[id, data]]);
}

export const getCaretColor = (diff) => {
  if (diff > 0) return "var(--turq)";
  if (diff < 0) return "var(--red)";
  return "var(--shade2)";
};

const KDA_COLORS = {
  noKda: "var(--shade3)",
  kda1less: "#828790",
  kda1to2: "#978D87",
  kda2to4: "#C4A889",
  kda4to6: "#DEAF78",
  kda6to10: "#E6A85F",
  kda10plus: "#FF9417",
  perfect: "#ffbe05",
};

export function kdaColorStyle(kda) {
  switch (true) {
    case kda < 1:
      return KDA_COLORS.kda1less;
    case kda >= 1 && kda < 2:
      return KDA_COLORS.kda1to2;
    case kda >= 2 && kda < 4:
      return KDA_COLORS.kda2to4;
    case kda >= 4 && kda < 6:
      return KDA_COLORS.kda4to6;
    case kda >= 6 && kda < 10:
      return KDA_COLORS.kda6to10;
    case kda >= 10:
      return KDA_COLORS.kda10plus;
    case kda === "perfect":
    case kda === "Perfect":
      return KDA_COLORS.perfect;
    default:
      return KDA_COLORS.noKda;
  }
}

export const formatDuration = (milliseconds, format = "h:mm:ss") => {
  const normalizeTime = (time) =>
    time.toString().length === 1 ? `0${time}` : time;

  const secondsAmount = milliseconds / 1000;

  switch (format) {
    case "hh:mm:ss":
    default: {
      const hours = normalizeTime(Math.floor(secondsAmount / 3600));
      const minutes = normalizeTime(Math.floor((secondsAmount / 60) % 60));
      const seconds = normalizeTime(Math.floor(secondsAmount % 60));
      return `${hours}:${minutes}:${seconds}`;
    }
    case "h:mm:ss": {
      const hours = Math.floor(secondsAmount / 3600);
      const minutes = normalizeTime(Math.floor((secondsAmount / 60) % 60));
      const seconds = normalizeTime(Math.floor(secondsAmount % 60));
      return `${hours}:${minutes}:${seconds}`;
    }
    case "mm:ss": {
      const minutes = normalizeTime(Math.floor(secondsAmount / 60));
      const seconds = normalizeTime(Math.floor(secondsAmount % 60));
      return `${minutes}:${seconds}`;
    }
    case "m:ss": {
      const minutes = Math.floor(secondsAmount / 60);
      const seconds = normalizeTime(Math.floor(secondsAmount % 60));
      return `${minutes}:${seconds}`;
    }
    case "ss": {
      return `${secondsAmount}`;
    }
  }
};

export function getHubRoute(): HubRouteMeta | null {
  const { hubRoute } = router.RouteComponent ?? {};

  if (!hubRoute) return null;

  return hubRoute();
}

export function useHubRoute() {
  useRouteComponent();
  useTranslation();
  return getHubRoute();
}

export type NavProfile = {
  name: string;
  imgUrl: string;
  profileUrl: string;
  tagLine?: string;
  shard?: string;
};

type LoggedIn<Game extends keyof AppSettings["loggedInAccounts"]> =
  AppSettings["loggedInAccounts"][Game][string];

export function getNavProfile({
  gameSymbol,
  account,
}: {
  gameSymbol: GameSymbol;
  account: AppSettings["loggedInAccounts"][keyof AppSettings["loggedInAccounts"]]["x"];
}): NavProfile {
  // Please use LoggedIn generic for type narrowing for your games to prevent issues until all games are properly typed in app-settings
  switch (gameSymbol) {
    case GAME_SYMBOL_LOL:
    case GAME_SYMBOL_TFT: {
      const { region, displayName, gameName, tagLine } =
        account as LoggedIn<"lol">;
      return {
        name: gameName || displayName,
        // @ts-ignore Todo: Refactor the typings for getGameProfileImg to accept account from AppSettings
        imgUrl: getGameProfileImg(gameSymbol, account),
        profileUrl: `/${GAME_SHORT_NAMES[gameSymbol]}/profile/${region}/${
          gameName && tagLine ? `${gameName}-${tagLine}` : displayName
        }`,
        shard: SERVICES_TO_REGIONS[region],
      };
    }
    case GAME_SYMBOL_APEX: {
      const { profileId, username } = account as LoggedIn<"apex">;
      const profileUrl = `/${GAME_SHORT_NAMES[gameSymbol]}/profile/${profileId}`;
      return {
        name: username,
        // @ts-ignore
        imgUrl: getGameProfileImg(gameSymbol, account),
        profileUrl,
      };
    }
    case GAME_SYMBOL_VAL: {
      const { name, imgUrl, tagLine, profileUrl } = formatProfileForNav(
        account as LoggedIn<"valorant">,
      );
      return {
        name,
        imgUrl,
        profileUrl,
        tagLine,
      };
    }
    case GAME_SYMBOL_CS2: {
      const { steamId, username } = account as LoggedIn<"cs2">;
      return {
        name: username,
        // @ts-ignore
        imgUrl: getGameProfileImg(gameSymbol, account),
        profileUrl: `/${GAME_SHORT_NAMES[gameSymbol]}/profile/${steamId}`,
      };
    }
    case GAME_SYMBOL_EFT: {
      const { accountId, nickname } = account as LoggedIn<"eft">;
      return {
        name: nickname,
        // @ts-ignore
        imgUrl: getGameProfileImg(gameSymbol, account),
        profileUrl: `/${GAME_SHORT_NAMES[gameSymbol]}/profile/${accountId}`,
      };
    }
    case GAME_SYMBOL_FORTNITE: {
      const { accountId, name } = account as LoggedIn<"fortnite">;
      return {
        name,
        // @ts-ignore
        imgUrl: getGameProfileImg(gameSymbol, account),
        profileUrl: `/${GAME_SHORT_NAMES[gameSymbol]}/profile/${accountId}`,
      };
    }
    case GAME_SYMBOL_DEADLOCK: {
      const {
        name,
        avatar: imgUrl,
        steam_id_3,
      } = account as LoggedIn<"deadlock">;
      return {
        name,
        imgUrl,
        profileUrl: `/${GAME_SHORT_NAMES[gameSymbol]}/profile/${steam_id_3}`,
      };
    }
    case GAME_SYMBOL_MARVEL_RIVALS: {
      const { playerId, name, currentHeroId } =
        account as LoggedIn<"marvel-rivals">;
      return {
        name,
        imgUrl: `${appURLs.CDN}/marvelrivals/${currentHeroId}.webp`,
        profileUrl: `/${GAME_SHORT_NAMES[gameSymbol]}/profile/${playerId}`,
      };
    }
  }
}

type LoggedInAccounts =
  AppSettings["loggedInAccounts"][keyof AppSettings["loggedInAccounts"]];

export function getAccountFromLoggedInAccounts(
  loggedInAccounts: AppSettings["loggedInAccounts"],
  gameShortName: GamesWithProp<"shortName">["shortName"],
  profileId: string = "",
): LoggedInAccounts["x"] | undefined {
  if (isEmpty(loggedInAccounts[gameShortName])) return;
  const account = loggedInAccounts[gameShortName][
    profileId
  ] as unknown as object;
  if (isEmpty(account))
    return Object.values(loggedInAccounts[gameShortName])[0] as
      | LoggedInAccounts["x"]
      | undefined;
  return account as LoggedInAccounts["x"];
}

export function useCurrentAccount<
  T extends keyof AppSettings["loggedInAccounts"],
>(gameSymbol: GameSymbol): LoggedIn<T> | undefined {
  const {
    localState,
    settings: { lastLoggedInIdByGame, loggedInAccounts },
  } = useSnapshot(readState);
  let gameShortName = gameShortNameReassign(GAME_SHORT_NAMES[gameSymbol]);

  if (gameShortName === "tft") gameShortName = "lol";

  const profileId =
    localState?.currentProfileId?.[gameShortName] ||
    lastLoggedInIdByGame[gameShortName];

  return getAccountFromLoggedInAccounts(
    loggedInAccounts as AppSettings["loggedInAccounts"],
    gameShortName,
    profileId,
  ) as LoggedIn<T>;
}

export const possibleUpdateStateValues = [
  "backgroundUpdate-green",
  "backgroundUpdate-yellow",
  "backgroundUpdate-red",
  "updateDownloaded",
  "ota-available",
];

export function getModifier(modifiers) {
  if (!modifiers || modifiers?.ctrlKey) {
    return "ctrl";
  } else if (modifiers?.shiftKey) {
    return "shift";
  } else if (modifiers?.altKey) {
    return "alt";
  }

  return null;
}

type LastPlayedGames = Array<keyof AppSettings["lastPlayedGames"]>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LocalState = { currentGame: GameSymbol; currentProfileId: GameHash<any> };

export function getLastPlayedGames(
  lastPlayedGames: AppSettings["lastPlayedGames"],
): LastPlayedGames {
  return Object.keys(lastPlayedGames)
    .filter((g) => ACTIVE_GAMES.includes(GAME_SYMBOL_BY_SHORT_NAME[g]))
    .sort(
      (a, b) => (lastPlayedGames[b] || 0) - (lastPlayedGames[a] || 0),
    ) as LastPlayedGames;
}
export const getRedirectUrl = (
  settings: AppSettings,
  localState: LocalState,
): string => fixPathname(_getRedirectUrl(settings, localState));

function _getRedirectUrl(
  settings: AppSettings,
  localState: LocalState,
): string | undefined {
  if (!settings) return;
  // State
  const { lastPlayedGames, loggedInAccounts, lastLoggedInIdByGame } = settings;
  const { currentGame, currentProfileId } = localState || {};

  // Game short name
  let shortName;

  // Determine the game short name
  const lastPlayedShortName = getLastPlayedGames(lastPlayedGames)[0];
  const shortNameByLastPlayedOrCurrent =
    lastPlayedShortName || (currentGame && GAME_SHORT_NAMES[currentGame]);
  if (shortNameByLastPlayedOrCurrent) {
    shortName = gameShortNameReassign(shortNameByLastPlayedOrCurrent);
  }

  if (shortName) {
    shortName = shortName === "tft" ? "lol" : shortName;

    const profileId =
      lastLoggedInIdByGame?.[shortName] || currentProfileId?.[shortName];

    const account = getAccountFromLoggedInAccounts(
      loggedInAccounts,
      shortName,
      profileId,
    );

    if (account) {
      const profileUrl = getNavProfile({
        account,
        gameSymbol: GAME_SYMBOL_BY_SHORT_NAME[shortName],
      })?.profileUrl;

      if (profileUrl) return profileUrl;
    }
  }

  for (const gameShortName in loggedInAccounts) {
    const shortName = gameShortName as GamesWithProp<"shortName">["shortName"];
    const account = getAccountFromLoggedInAccounts(loggedInAccounts, shortName);
    if (account) {
      const profileUrl = getNavProfile({
        account,
        gameSymbol: GAME_SYMBOL_BY_SHORT_NAME[gameShortNameReassign(shortName)],
      })?.profileUrl;
      if (profileUrl) return profileUrl;
    }
  }
}

const REGEXP_BAD_PROFILE_URL = /(?:\/)?(\w+)\/profile(?:\/)?$/;

/**
 * Prevents returning a `/{game}/profile/` pathname
 */
export function fixPathname(pathname: string | undefined): string | undefined {
  if (typeof pathname === "string" && REGEXP_BAD_PROFILE_URL.test(pathname)) {
    const shortName = pathname.match(REGEXP_BAD_PROFILE_URL)?.[1];
    if (shortName) return `/${shortName}`;
    return "/";
  }
  return pathname;
}
